import React, { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";

const DISMISS_KEY = "bailey_pwa_dismissed_v1";
const DISMISS_DAYS = 14;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function dismissNow() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

// Bottom-anchored install prompt. On Android/Chrome we drive the native
// beforeinstallprompt flow; on iOS we show manual "Add to Home Screen" steps.
export default function PwaInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [iosShow, setIosShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari never fires beforeinstallprompt — show manual banner after a beat.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setIosShow(true), 1200);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (outcome === "dismissed") dismissNow();
    setDeferred(null);
  };

  const close = () => {
    dismissNow();
    setDeferred(null);
    setIosShow(false);
  };

  if (deferred) {
    return (
      <div
        className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-96 z-50 rounded-2xl bg-[#1a2947] text-white shadow-[0_20px_50px_-15px_rgba(26,41,71,0.6)] border border-[#1a2947]/40 p-4 pr-3 flex items-start gap-3"
        role="dialog"
        data-testid="pwa-install-banner"
      >
        <div className="mt-0.5 h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <Download className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">
            Install
          </div>
          <div className="font-serif text-lg leading-snug mt-0.5">
            Add the reunion to your home screen
          </div>
          <p className="text-xs text-white/70 mt-1">
            One tap to open your family card next time — no browser tabs to hunt for.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={install}
              className="rounded-full bg-white text-[#1a2947] text-sm font-semibold px-4 py-2 hover:bg-white/90"
              data-testid="pwa-install-accept"
            >
              Install
            </button>
            <button
              type="button"
              onClick={close}
              className="text-xs text-white/70 hover:text-white underline"
              data-testid="pwa-install-later"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="text-white/60 hover:text-white p-1"
          data-testid="pwa-install-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (iosShow) {
    return (
      <div
        className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-96 z-50 rounded-2xl bg-white border border-[#e6e2d6] shadow-[0_20px_50px_-15px_rgba(26,41,71,0.35)] p-4 pr-3 flex items-start gap-3"
        role="dialog"
        data-testid="pwa-install-banner-ios"
      >
        <div className="mt-0.5 h-9 w-9 rounded-xl bg-[#faf7ef] border border-[#eae5d9] flex items-center justify-center shrink-0 text-[#1a2947]">
          <Download className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[#8a7a3a] font-semibold">
            Install on iPhone
          </div>
          <div className="font-serif text-lg text-stone-900 leading-snug mt-0.5">
            Add to your Home Screen
          </div>
          <div className="mt-2 space-y-1.5 text-sm text-stone-700">
            <div className="flex items-center gap-2">
              <span className="text-stone-400">1.</span> Tap
              <Share className="h-4 w-4 text-[#1a2947]" /> in Safari.
            </div>
            <div className="flex items-center gap-2">
              <span className="text-stone-400">2.</span> Choose
              <PlusSquare className="h-4 w-4 text-[#1a2947]" />
              <span className="font-medium">Add to Home Screen</span>.
            </div>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={close}
              className="text-xs text-stone-500 hover:text-stone-800 underline"
              data-testid="pwa-install-ios-dismiss"
            >
              Got it — hide this
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="text-stone-400 hover:text-stone-700 p-1"
          data-testid="pwa-install-ios-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
}
